import React, {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import {toast} from "react-toastify";
import {UNSAFE_DataRouterContext, useBlocker} from "react-router-dom";

/**
 * Два контекста:
 *  - Registry — стабильные register/notify для полей (не вызывает лишних
 *    перерисовок полей при каждом изменении);
 *  - State — состояние для панели сохранения (dirtyCount, saving, saveAll).
 */
const RegistryContext = createContext(null);
const StateContext = createContext(null);

export const useFormSaveRegistration = () => useContext(RegistryContext);
export const useFormSaveState = () => useContext(StateContext);

const collectDirty = (fields) => Array.from(fields.values()).filter((field) => field.isDirty());

const CONFIRM_LEAVE = "На странице есть несохранённые изменения. Перейти и потерять их?";

/**
 * Блокировка SPA-переходов (ссылки, navigate(), кнопки «назад/вперёд»).
 *
 * `useBlocker` существует только под data router (`createBrowserRouter` в App.js).
 * В тестах формы рендерятся в обычном `MemoryRouter`, поэтому компонент просто
 * не монтируется — иначе хук бросил бы исключение.
 */
const NavigationBlocker = ({onDiscard}) => {
    const blocker = useBlocker(true);

    useEffect(() => {
        if (blocker.state !== "blocked") {
            return;
        }
        if (window.confirm(CONFIRM_LEAVE)) {
            onDiscard();
            blocker.proceed();
        } else {
            blocker.reset();
        }
    }, [blocker, onDiscard]);

    return null;
};

export const FormSaveProvider = ({children}) => {
    const fieldsRef = useRef(new Map());
    const [version, setVersion] = useState(0);
    const [saving, setSaving] = useState(false);
    const dataRouter = useContext(UNSAFE_DataRouterContext);
    const canBlock = Boolean(dataRouter);

    const notify = useCallback(() => {
        setVersion((value) => value + 1);
    }, []);

    const register = useCallback((key, field) => {
        fieldsRef.current.set(key, field);
        notify();
        return () => {
            fieldsRef.current.delete(key);
            notify();
        };
    }, [notify]);

    // version в зависимостях — чтобы пересчитать dirty после notify.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const dirtyCount = useMemo(() => collectDirty(fieldsRef.current).length, [version]);

    const saveAll = useCallback(async () => {
        const dirty = collectDirty(fieldsRef.current);
        if (dirty.length === 0) {
            return {saved: 0, failed: []};
        }

        setSaving(true);
        const failed = [];
        let saved = 0;

        // Последовательно: старые API не поддерживают атомарное сохранение,
        // параллельные запросы могут перезаписать друг друга.
        for (const field of dirty) {
            try {
                // eslint-disable-next-line no-await-in-loop
                await field.save();
                saved += 1;
            } catch (error) {
                failed.push({label: field.label || "поле", error});
            }
        }

        setSaving(false);
        notify();

        if (failed.length > 0) {
            const labels = failed.map((item) => item.label).join(", ");
            toast.error(`Не удалось сохранить: ${labels}`);
        } else {
            toast.success(saved === 1 ? "Изменения сохранены" : `Сохранено полей: ${saved}`);
        }

        return {saved, failed};
    }, [notify]);

    const resetAll = useCallback(() => {
        collectDirty(fieldsRef.current).forEach((field) => field.reset());
        notify();
    }, [notify]);

    useEffect(() => {
        if (dirtyCount === 0) {
            return undefined;
        }
        const onBeforeUnload = (event) => {
            event.preventDefault();
            event.returnValue = "";
        };
        window.addEventListener("beforeunload", onBeforeUnload);
        return () => window.removeEventListener("beforeunload", onBeforeUnload);
    }, [dirtyCount]);

    // SPA-переходы по ссылкам. Под data router их (включая navigate() и
    // кнопки «назад/вперёд») ловит NavigationBlocker — ручной обработчик дал бы
    // двойное подтверждение. Здесь он остаётся запасным путём вне data router.
    useEffect(() => {
        if (canBlock || dirtyCount === 0) {
            return undefined;
        }
        const onClick = (event) => {
            if (event.defaultPrevented || event.button !== 0 ||
                event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
                return;
            }
            const target = event.target;
            if (!(target instanceof Element)) {
                return;
            }
            const anchor = target.closest("a[href]");
            if (!anchor || anchor.hasAttribute("download")) {
                return;
            }
            if (anchor.target && anchor.target !== "_self") {
                return;
            }
            const url = new URL(anchor.href, window.location.origin);
            if (url.origin !== window.location.origin) {
                return;
            }
            if (url.pathname === window.location.pathname && url.search === window.location.search) {
                return;
            }
            if (!window.confirm("На странице есть несохранённые изменения. Перейти и потерять их?")) {
                event.preventDefault();
                event.stopPropagation();
                return;
            }
            resetAll();
        };
        document.addEventListener("click", onClick, true);
        return () => document.removeEventListener("click", onClick, true);
    }, [canBlock, dirtyCount, resetAll]);

    useEffect(() => {
        const onKeyDown = (event) => {
            const isSave = (event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "s";
            if (!isSave) {
                return;
            }
            event.preventDefault();
            if (!saving && collectDirty(fieldsRef.current).length > 0) {
                saveAll();
            }
        };
        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, [saveAll, saving]);

    const registry = useMemo(() => ({register, notify}), [register, notify]);
    const state = useMemo(() => ({
        version,
        saving,
        dirtyCount,
        saveAll,
        resetAll
    }), [version, saving, dirtyCount, saveAll, resetAll]);

    return (
        <RegistryContext.Provider value={registry}>
            <StateContext.Provider value={state}>
                {canBlock && dirtyCount > 0 ? <NavigationBlocker onDiscard={resetAll}/> : null}
                {children}
            </StateContext.Provider>
        </RegistryContext.Provider>
    );
};

export default FormSaveProvider;
